import { PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { useState } from 'react';

import { Container, Section } from 'components/GridV2';
import CustomPortableText from 'components/rebrand/CustomPortableText';

interface Props {
  data: {
    headers: string[];
    rows: {
      title: string;
      content: string[];
    }[];
    heading: PortableTextBlock | PortableTextBlock[];
  };
}

const Table = ({ data }: Props) => {
  return (
    <Section className="bg-white py-64">
      <Container className="px-32 xs:px-0">
        <div className="mb-60 text-center [&_h2]:mx-auto [&_h2]:mb-20 [&_h2]:w-[75%] [&_h2]:text-primary-400 md:[&_h2]:w-[70%] [&_h2_strong]:text-primary-200 [&_p]:text-xl">
          <CustomPortableText content={data.heading} />
        </div>
        <div className="hidden md:block">
          <DesktopTable data={data} />
        </div>
        <div className="block md:hidden">
          <MobileTable data={data} />
        </div>
      </Container>
    </Section>
  );
};

export default Table;

const MobileTable = ({ data }: Props) => {
  const [indexToShow, setIndexToShow] = useState(0);
  return (
    <table className="mx-auto w-full max-w-[450px] border-separate border-spacing-0 bg-none">
      <thead className="[&_th:first-of-type]:rounded-tl-12 [&_th:last-of-type]:rounded-tr-12">
        <tr className="grid grid-cols-3 text-primary-400 [&>th:first-of-type]:border-l [&>th]:flex [&>th]:items-center [&>th]:justify-center [&>th]:border-b [&>th]:border-r [&>th]:border-t [&>th]:border-grey-75 [&>th]:px-16 [&>th]:py-10 [&>th]:text-center [&>th]:text-[10px]">
          <th
            className={clsx(
              indexToShow === 0 ? 'bg-secondary-50 font-bold text-success-500' : 'bg-white font-medium text-grey-450',
            )}
            onClick={() => setIndexToShow(0)}
          >
            {data.headers[0]}
          </th>
          <th
            className={clsx(
              indexToShow === 1 ? 'bg-secondary-50 font-bold text-success-500' : 'bg-white font-medium text-grey-450',
            )}
            onClick={() => setIndexToShow(1)}
          >
            {data.headers[1]}
          </th>
          <th
            className={clsx(
              indexToShow === 2 ? 'bg-secondary-50 font-bold text-success-500' : 'bg-white font-medium text-grey-450',
            )}
            onClick={() => setIndexToShow(2)}
          >
            {data.headers[2]}
          </th>
        </tr>
      </thead>
      <tbody className="[&_tr:last-of-type_td:first-of-type]:rounded-bl-12 [&_tr:last-of-type_td:last-of-type]:rounded-br-12">
        {data.rows.map((row, i) => (
          <tr
            key={i}
            className="grid grid-cols-2 [&>td:first-of-type]:border-l [&>td:not(:first-of-type)]:text-center [&>td]:border-b [&>td]:border-r [&>td]:border-grey-75 [&>td]:px-16 [&>td]:py-24 [&>td]:text-sm"
          >
            <td className="font-medium text-grey-500">{row.title}</td>
            <td className="bg-secondary-50 font-medium text-success-500">{row.content[indexToShow]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const DesktopTable = ({ data }: Props) => {
  return (
    <table className="w-full border-separate border-spacing-0 bg-none">
      <thead className="[&_th:first-of-type]:rounded-tl-12 [&_th:last-of-type]:rounded-tr-12">
        <tr className="text-sm text-primary-400 [&>th:first-of-type]:border-l [&>th:nth-child(2)]:font-bold [&>th]:border-b [&>th]:border-r [&>th]:border-t [&>th]:border-grey-75 [&>th]:px-24 [&>th]:py-26 [&>th]:text-center [&>th]:text-md [&>th]:font-medium">
          <th className="w-[16%] bg-white" />
          <th className="w-[28%] bg-secondary-50 text-success-500">{data.headers[0]}</th>
          <th className="w-[28%] bg-white text-grey-450">{data.headers[1]}</th>
          <th className="w-[28%] bg-white text-grey-450">{data.headers[2]}</th>
        </tr>
      </thead>
      <tbody className="[&_tr:last-of-type_td:first-of-type]:rounded-bl-12 [&_tr:last-of-type_td:last-of-type]:rounded-br-12">
        {data.rows.map((row, i) => (
          <tr
            key={i}
            className="[&>td:first-of-type]:border-l [&>td:not(:first-of-type)]:text-center [&>td]:border-b [&>td]:border-r [&>td]:border-grey-75 [&>td]:px-24 [&>td]:py-24 [&>td]:text-sm"
          >
            <td className="w-[16%] bg-white font-medium text-grey-500">{row.title}</td>
            <td className="w-[28%] bg-secondary-50 font-medium text-success-500">{row.content[0]}</td>
            <td className="w-[28%] bg-white text-grey-350">{row.content[1]}</td>
            <td className="w-[28%] bg-white text-grey-350">{row.content[2]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
